import { FC, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useFetchActiveProcedureDetails } from "@/features/procedures/api/procedure.queries";
import { useQueryParamHide } from "@/features/procedures/hooks/useQueryParamHide";
import { ProcedureDetailNode } from "@/features/procedures/model/types/node";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Box, IconButton, Typography, useTheme } from "@mui/material";

import { RouteParams } from "@/service/Utilities";

interface ChecklistNavigationControlsProps {
	activeSchemeId: number | undefined;
	setActiveSchemeId: (schemeId: number) => void;
	wrapControls?: boolean;
	compact?: boolean;
	/** Pass procedure ID - prev/next always use /procedure/{procedureId}/checklist/{schemeId} */
	procedureId?: number;
}

export const ChecklistNavigationControls: FC<
	ChecklistNavigationControlsProps
> = (props) => {
	const [activeChildNode, setActiveChildNode] = useState<
		ProcedureDetailNode | undefined
	>();
	const { id: idParam, schemeId } = useParams<RouteParams>();
	const checklistId = parseInt(schemeId ?? "0", 10);
	const procedureIdFromRoute = idParam ? parseInt(idParam, 10) : undefined;
	const resolvedProcedureId = props.procedureId ?? procedureIdFromRoute;
	const [hide, setHide] = useState("");
	const queryHide = useQueryParamHide();
	const { data: currentActiveDetails } =
		useFetchActiveProcedureDetails(resolvedProcedureId);

	useEffect(() => {
		setHide(queryHide ? "hide=1" : "");
	}, [queryHide]);

	useEffect(() => {
		if (!currentActiveDetails) {
			return;
		}

		const parentNode = currentActiveDetails?.ProcedureDetailNodes.find((node) =>
			node.Children.find((child) => child.SchemeId === checklistId),
		);
		const thisNode = parentNode?.Children.find(
			(child) => child.SchemeId === checklistId,
		);

		if (thisNode?.SchemeId && thisNode.SchemeId !== activeChildNode?.SchemeId) {
			setActiveChildNode(thisNode);

			if (props.activeSchemeId !== thisNode.SchemeId) {
				props.setActiveSchemeId(thisNode.SchemeId);
			}
		}
	}, [currentActiveDetails, checklistId, activeChildNode?.SchemeId]);

	const getClassName = (nextNodeId: number | undefined) => {
		return nextNodeId ? "button-icon" : "button-icon button--inactive";
	};

	const theme = useTheme();
	const iconSize = props.compact ? "small" : "small";
	const btnSize = props.compact ? "small" : "medium";

	const navButtonSx = {
		color: theme.palette.text.secondary,
		"&:hover:not(.Mui-disabled)": {
			color: theme.palette.text.primary,
			backgroundColor: theme.palette.action.hover,
		},
		"&.Mui-disabled": {
			color: theme.palette.text.disabled,
		},
	};

	const { index, total, previousSchemeId, nextSchemeId } = useMemo(() => {
		if (!currentActiveDetails?.ProcedureDetailNodes || !activeChildNode) {
			return {
				index: 0,
				total: 0,
				previousSchemeId: undefined as number | undefined,
				nextSchemeId: undefined as number | undefined,
			};
		}
		const flat: { schemeId: number }[] = [];
		for (const node of currentActiveDetails.ProcedureDetailNodes) {
			for (const child of node.Children ?? []) {
				if (child.SchemeId && child.CheckListId)
					flat.push({ schemeId: child.SchemeId });
			}
		}
		const idx = flat.findIndex((c) => c.schemeId === activeChildNode.SchemeId);
		const prevFromFlat = idx > 0 ? flat[idx - 1]?.schemeId : undefined;
		const nextFromFlat =
			idx >= 0 && idx < flat.length - 1 ? flat[idx + 1]?.schemeId : undefined;
		return {
			index: idx >= 0 ? idx + 1 : 0,
			total: flat.length,
			previousSchemeId: activeChildNode.PreviousChecklist ?? prevFromFlat,
			nextSchemeId: activeChildNode.NextChecklist ?? nextFromFlat,
		};
	}, [
		currentActiveDetails?.ProcedureDetailNodes,
		activeChildNode?.SchemeId,
		activeChildNode?.PreviousChecklist,
		activeChildNode?.NextChecklist,
	]);

	const procedureId = props.procedureId ?? currentActiveDetails?.ProcedureId;
	const prevTo =
		procedureId && previousSchemeId != null
			? `/procedure/${procedureId}/checklist/${previousSchemeId}${hide ? `?${hide}` : ""}`
			: undefined;
	const nextTo =
		procedureId && nextSchemeId != null
			? `/procedure/${procedureId}/checklist/${nextSchemeId}${hide ? `?${hide}` : ""}`
			: undefined;

	const navigationControls = (
		<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
			{previousSchemeId ? (
				prevTo ? (
					<IconButton
						size={btnSize}
						component={Link}
						to={prevTo}
						className={getClassName(previousSchemeId)}
						aria-label="Previous checklist"
						sx={navButtonSx}
					>
						<ArrowBackIosIcon fontSize={iconSize} />
					</IconButton>
				) : (
					<IconButton
						size={btnSize}
						disabled
						className="button-icon button--inactive"
						aria-label="Previous checklist (loading)"
						sx={navButtonSx}
					>
						<ArrowBackIosIcon fontSize={iconSize} />
					</IconButton>
				)
			) : (
				<IconButton
					size={btnSize}
					disabled
					className="button-icon button--inactive"
					aria-label="Previous checklist (none)"
					sx={navButtonSx}
				>
					<ArrowBackIosIcon fontSize={iconSize} />
				</IconButton>
			)}
			{total > 1 && (
				<Typography
					variant="caption"
					sx={{
						color: "text.secondary",
						fontWeight: 600,
						minWidth: 32,
						textAlign: "center",
					}}
				>
					{index}/{total}
				</Typography>
			)}
			{nextSchemeId ? (
				nextTo ? (
					<IconButton
						size={btnSize}
						component={Link}
						to={nextTo}
						className={getClassName(nextSchemeId)}
						aria-label="Next checklist"
						sx={navButtonSx}
					>
						<ArrowForwardIosIcon fontSize={iconSize} />
					</IconButton>
				) : (
					<IconButton
						size={btnSize}
						disabled
						className="button-icon button--inactive"
						aria-label="Next checklist (loading)"
						sx={navButtonSx}
					>
						<ArrowForwardIosIcon fontSize={iconSize} />
					</IconButton>
				)
			) : (
				<IconButton
					size={btnSize}
					disabled
					className="button-icon button--inactive"
					aria-label="Next checklist (none)"
					sx={navButtonSx}
				>
					<ArrowForwardIosIcon fontSize={iconSize} />
				</IconButton>
			)}
		</Box>
	);

	if (props.wrapControls) {
		return (
			<div className="heading-wrapper">
				<div className="heading-wrapper-tools">{navigationControls}</div>
			</div>
		);
	}

	return navigationControls;
};

export default ChecklistNavigationControls;
